import { useState, useCallback, useMemo } from 'react';
import { OnboardingStepId, ONBOARDING_STEPS, OnboardingProgress } from '../../types/onboarding';
import { onboardingStorage } from '../../services/Onboarding';
import { logger } from '../../utils/logger';

/**
 * useOnboarding Hook - Clean Edition
 * Manages the sequential flow of onboarding steps and ensures state persistence.
 */
export const useOnboarding = (initialStepId?: OnboardingStepId) => {
  // 1. Core State: The current active step ID
  const [currentStepId, setCurrentStepId] = useState<OnboardingStepId>(() => {
    // An explicit initial step wins: it's how the feed's completion prompt
    // opens the flow directly on the step that collects the missing field,
    // rather than making the user walk from "welcome" to find it.
    if (initialStepId && ONBOARDING_STEPS.some(s => s.id === initialStepId)) {
      return initialStepId;
    }
    const saved = onboardingStorage.getProgress();
    // Start at welcome if nothing saved, or the saved step
    return saved?.currentStep || 'welcome';
  });

  // 2. Progress State: Tracks completion and skipped status
  const [progress, setProgress] = useState<OnboardingProgress>(() => {
    const saved = onboardingStorage.getProgress();
    return saved || {
      currentStep: 'welcome',
      completedSteps: [],
      skippedSteps: [],
      stepData: {}
    };
  });

  // 3. Derived State: Helpers for UI/Navigation
  const currentStepIndex = useMemo(() => 
    ONBOARDING_STEPS.findIndex(s => s.id === currentStepId), 
    [currentStepId]
  );

  const currentStep = useMemo(() => {
    const step = ONBOARDING_STEPS[currentStepIndex];
    if (!step) {
      // Fallback to first step if index is invalid (e.g. storage has old step name)
      return ONBOARDING_STEPS[0];
    }
    return step;
  }, [currentStepIndex]);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === ONBOARDING_STEPS.length - 1;

  // 4. Persistence Actions: Atomic updates to prevent flicker
  const updateProgress = useCallback((updates: Partial<OnboardingProgress>) => {
    setProgress(prev => {
      const next = { ...prev, ...updates };
      onboardingStorage.saveProgressState(next);
      return next;
    });
  }, []);

  // 5. Navigation Actions
  const goToNextStep = useCallback(() => {
    if (isLastStep) return;
    
    const nextStepId = ONBOARDING_STEPS[currentStepIndex + 1].id;
    
    // Atomic update: jump to next, mark current as complete. Also clears the
    // step out of skippedSteps — otherwise a step that was skipped once, then
    // completed after navigating back, gets double-counted in both arrays.
    setCurrentStepId(nextStepId);
    updateProgress({
      currentStep: nextStepId,
      completedSteps: Array.from(new Set([...progress.completedSteps, currentStepId])),
      skippedSteps: progress.skippedSteps.filter(id => id !== currentStepId)
    });

    logger.log(`[ONBOARDING] Navigating to: ${nextStepId}`);
  }, [currentStepId, currentStepIndex, isLastStep, progress.completedSteps, progress.skippedSteps, updateProgress]);

  const goToPreviousStep = useCallback(() => {
    if (isFirstStep) return;
    
    const prevStepId = ONBOARDING_STEPS[currentStepIndex - 1].id;
    
    setCurrentStepId(prevStepId);
    updateProgress({ currentStep: prevStepId });
    
    logger.log(`[ONBOARDING] Navigating back to: ${prevStepId}`);
  }, [currentStepIndex, isFirstStep, updateProgress]);

  // Jump straight to a step without marking anything complete or skipped —
  // used when entering the flow to fix one specific gap.
  const goToStep = useCallback((stepId: OnboardingStepId) => {
    if (!ONBOARDING_STEPS.some(s => s.id === stepId)) return;
    setCurrentStepId(stepId);
    updateProgress({ currentStep: stepId });
    logger.log(`[ONBOARDING] Jumping to: ${stepId}`);
  }, [updateProgress]);

  const skipToNextStep = useCallback(() => {
    if (isLastStep || !currentStep.canSkip) return;
    
    const nextStepId = ONBOARDING_STEPS[currentStepIndex + 1].id;
    
    setCurrentStepId(nextStepId);
    updateProgress({
      currentStep: nextStepId,
      skippedSteps: Array.from(new Set([...progress.skippedSteps, currentStepId])),
      completedSteps: progress.completedSteps.filter(id => id !== currentStepId)
    });

    logger.log(`[ONBOARDING] Skipping step: ${currentStepId}`);
  }, [currentStep.canSkip, currentStepId, currentStepIndex, isLastStep, progress.completedSteps, progress.skippedSteps, updateProgress]);

  // 6. Data Management
  const saveStepData = useCallback((stepId: OnboardingStepId, data: any) => {
    // Save to actual storage immediately
    onboardingStorage.saveProgress(stepId, data);
    
    // Update local state record
    updateProgress({
      stepData: {
        ...progress.stepData,
        [stepId]: data
      }
    });
  }, [progress.stepData, updateProgress]);

  const getStepData = useCallback((stepId: OnboardingStepId) => {
    return progress.stepData[stepId] || onboardingStorage.getStepData(stepId);
  }, [progress.stepData]);

  const completeOnboarding = useCallback(() => {
    onboardingStorage.completeOnboarding();
    logger.log('[ONBOARDING] Flow completed successfully');
  }, []);

  return {
    // Current State
    currentStepId,
    currentStep,
    progress,
    
    // Status
    isFirstStep,
    isLastStep,
    
    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    skipToNextStep,
    saveStepData,
    getStepData,
    completeOnboarding
  };
};
